import React from 'react';

import FormikTextRow, {
  FormikTextRowType,
} from 'components/Common/Formik/Row/FormikTextRow';
import FormikRadioGroupRow from 'components/Common/Formik/Row/FormikRadioGroupRow';
import TelephoneRowComponent from 'components/Common/Formik/Row/RowComponent/TelephoneRowComponent';
import { Field, RadioField, RepeatTextField, TextField } from 'types/Field';

export type RowBuilderProps = {
  field: Field &
    Pick<FormikTextRowType, 'afterOnBlur' | 'beforeOnChange' | 'afterOnChange'>;
  styles?: Record<string, string>;
};

function RowBuilder({
  field: { component: Component, ...field },
  styles,
}: RowBuilderProps): JSX.Element | null {
  if (Component) return <Component styles={styles} field={field} />;

  const type = field.type;
  if ('repeated' === type) {
    const {
      required,
      disabled,
      autoComplete,
      repeatedType,
      inputProps,
      label,
      name,
      placeholder,
      id,
      secondLabel,
      secondName,
      secondPlaceholder,
      secondId,
      type, // eslint-disable-line @typescript-eslint/no-unused-vars -- Extracted for non-reuse.
      ...fieldByType
    } = field as RepeatTextField;

    const commonProps = {
      required: required,
      disabled: disabled,
      autoComplete: autoComplete,
      ...inputProps,
    };

    return (
      <>
        <FormikTextRow
          label={label}
          classes={getClasses(field.classes, styles)}
          inputProps={{
            ...commonProps,
            name: name,
            id: id,
            placeholder: placeholder,
            type: repeatedType,
          }}
          {...fieldByType}
        />
        <FormikTextRow
          label={secondLabel}
          classes={getClasses(field.classes, styles)}
          inputProps={{
            ...commonProps,
            name: secondName,
            id: secondId,
            placeholder: secondPlaceholder,
            type: repeatedType,
          }}
          {...fieldByType}
        />
      </>
    );
  }

  if ('radio' === type) {
    const fieldByType = field as RadioField;
    const options = fieldByType.options.map(option => ({
      ...option,
      id: option.id || `${fieldByType.name}_${option.value}`,
      required: fieldByType.required,
    }));

    return (
      <FormikRadioGroupRow
        name={fieldByType.name}
        label={fieldByType.label}
        options={options}
        classes={getClasses(field.classes, styles)}
      />
    );
  }

  if (['text', 'password', 'email', 'tel'].includes(type)) {
    const {
      onChange: onChangeField,
      classes,
      inputProps,
      label,
      autoComplete,
      disabled,
      name,
      type: fieldType,
      placeholder,
      id,
      required,
      ...fieldByType
    } = field as TextField;

    return (
      <FormikTextRow
        label={label}
        classes={getClasses(classes, styles)}
        component={fieldType === 'tel' ? TelephoneRowComponent : undefined}
        aroundOnChange={(onChange, event) => {
          const el = event.target as HTMLInputElement;

          if (onChangeField) {
            onChangeField(val => {
              el.value = val;
              onChange(event);
            }, el.value);
          } else {
            onChange(event);
          }
        }}
        inputProps={{
          name: name,
          disabled: disabled,
          autoComplete: autoComplete as string,
          placeholder: placeholder,
          id: id,
          required: required,
          type: 'tel' === type ? 'text' : type,
          ...inputProps,
        }}
        {...fieldByType}
      />
    );
  }

  return null;
}

export default React.memo(RowBuilder);

export function getClasses(
  classes: Field['classes'],
  styles?: Record<string, string>
): Record<string, unknown> | undefined {
  if (typeof classes === 'function' && styles) {
    return classes(styles);
  } else if (typeof classes === 'object') {
    return classes;
  }

  return undefined;
}
